// Offline storage utility using IndexedDB
const DB_NAME = 'GustoWholesaleDB';
const DB_VERSION = 2;

class OfflineStorage {
  constructor() {
    this.db = null;
    this.isOnline = navigator.onLine;
    
    // Listen for online/offline events
    window.addEventListener('online', () => {
      this.isOnline = true;
      window.dispatchEvent(new CustomEvent('connectionChange', { detail: { online: true } }));
    });
    
    window.addEventListener('offline', () => {
      this.isOnline = false;
      window.dispatchEvent(new CustomEvent('connectionChange', { detail: { online: false } }));
    });
  }

  async init() {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);
      
      request.onerror = () => reject(request.error);
      request.onsuccess = () => {
        this.db = request.result;
        resolve(this.db);
      };
      
      request.onupgradeneeded = (event) => {
        const db = event.target.result;
        
        // Create stores
        if (!db.objectStoreNames.contains('products')) {
          db.createObjectStore('products', { keyPath: 'id' });
        }
        if (!db.objectStoreNames.contains('categories')) {
          db.createObjectStore('categories', { keyPath: 'id' });
        }
        if (!db.objectStoreNames.contains('metadata')) {
          db.createObjectStore('metadata', { keyPath: 'key' });
        }
        if (!db.objectStoreNames.contains('pending_orders')) {
          // Auto-incrementing id; each entry: {id, payload, source, created_at, attempts, last_error}
          db.createObjectStore('pending_orders', { keyPath: 'id', autoIncrement: true });
        }
      };
    });
  }

  async saveProducts(products) {
    if (!this.db) await this.init();
    
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction(['products', 'metadata'], 'readwrite');
      const store = transaction.objectStore('products');
      const metaStore = transaction.objectStore('metadata');
      
      // Clear existing products
      store.clear();
      
      // Add all products
      products.forEach(product => {
        store.add(product);
      });
      
      // Save last sync time
      metaStore.put({ key: 'lastProductSync', value: new Date().toISOString() });
      
      transaction.oncomplete = () => resolve();
      transaction.onerror = () => reject(transaction.error);
    });
  }

  async getProducts() {
    if (!this.db) await this.init();
    
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction(['products'], 'readonly');
      const store = transaction.objectStore('products');
      const request = store.getAll();
      
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  async saveCategories(categories) {
    if (!this.db) await this.init();
    
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction(['categories', 'metadata'], 'readwrite');
      const store = transaction.objectStore('categories');
      const metaStore = transaction.objectStore('metadata');
      
      // Clear existing categories
      store.clear();
      
      // Add all categories
      categories.forEach(category => {
        store.add(category);
      });
      
      // Save last sync time
      metaStore.put({ key: 'lastCategorySync', value: new Date().toISOString() });
      
      transaction.oncomplete = () => resolve();
      transaction.onerror = () => reject(transaction.error);
    });
  }

  async getCategories() {
    if (!this.db) await this.init();
    
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction(['categories'], 'readonly');
      const store = transaction.objectStore('categories');
      const request = store.getAll();
      
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  async getLastSync() {
    if (!this.db) await this.init();
    
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction(['metadata'], 'readonly');
      const store = transaction.objectStore('metadata');
      const request = store.get('lastProductSync');
      
      request.onsuccess = () => resolve(request.result?.value || null);
      request.onerror = () => reject(request.error);
    });
  }

  async clearAll() {
    if (!this.db) await this.init();
    
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction(['products', 'categories', 'metadata'], 'readwrite');
      
      transaction.objectStore('products').clear();
      transaction.objectStore('categories').clear();
      transaction.objectStore('metadata').clear();
      
      transaction.oncomplete = () => resolve();
      transaction.onerror = () => reject(transaction.error);
    });
  }

  // ---- Pending orders queue (offline submit) -------------------------------

  async addPendingOrder(payload, source = 'cart', postActions = null) {
    if (!this.db) await this.init();
    return new Promise((resolve, reject) => {
      const tx = this.db.transaction(['pending_orders'], 'readwrite');
      const store = tx.objectStore('pending_orders');
      const entry = {
        payload,
        source,
        post_actions: Array.isArray(postActions) ? postActions : [],
        created_at: new Date().toISOString(),
        attempts: 0,
        last_error: null,
      };
      const req = store.add(entry);
      req.onsuccess = () => {
        window.dispatchEvent(new CustomEvent('pendingOrdersChanged'));
        resolve({ ...entry, id: req.result });
      };
      req.onerror = () => reject(req.error);
    });
  }

  async getPendingOrders() {
    if (!this.db) await this.init();
    return new Promise((resolve, reject) => {
      const tx = this.db.transaction(['pending_orders'], 'readonly');
      const req = tx.objectStore('pending_orders').getAll();
      req.onsuccess = () => resolve(req.result || []);
      req.onerror = () => reject(req.error);
    });
  }

  async countPendingOrders() {
    if (!this.db) await this.init();
    return new Promise((resolve, reject) => {
      const tx = this.db.transaction(['pending_orders'], 'readonly');
      const req = tx.objectStore('pending_orders').count();
      req.onsuccess = () => resolve(req.result || 0);
      req.onerror = () => reject(req.error);
    });
  }

  async removePendingOrder(id) {
    if (!this.db) await this.init();
    return new Promise((resolve, reject) => {
      const tx = this.db.transaction(['pending_orders'], 'readwrite');
      tx.objectStore('pending_orders').delete(id).onsuccess = () => {
        window.dispatchEvent(new CustomEvent('pendingOrdersChanged'));
        resolve(true);
      };
      tx.onerror = () => reject(tx.error);
    });
  }

  async markPendingOrderAttempt(id, errorMessage = null) {
    if (!this.db) await this.init();
    return new Promise((resolve, reject) => {
      const tx = this.db.transaction(['pending_orders'], 'readwrite');
      const store = tx.objectStore('pending_orders');
      const req = store.get(id);
      req.onsuccess = () => {
        const cur = req.result;
        if (!cur) return resolve(null);
        cur.attempts = (cur.attempts || 0) + 1;
        cur.last_error = errorMessage;
        cur.last_attempt_at = new Date().toISOString();
        store.put(cur).onsuccess = () => resolve(cur);
      };
      req.onerror = () => reject(req.error);
    });
  }
}

export const offlineStorage = new OfflineStorage();
export default offlineStorage;
